import React, { useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Modal from "components/Modal";
import Shadow from "components/Shadow";
import Button from "components/Button";
import ImgIcon from "components/imgIcon";
import GridView from "components/GridView";
import Separator from "components/Separator";
import InputLabel from "components/InputLabel";
import LoadingBox from "components/LoadingBox";
import ConfirmAlert from "components/ConfirmAlert";
import DeleteAlertComponent from "components/DeleteAlertComponent";

import { sizes, colors, strings, patterns } from "assets/constants";
import { icons } from "assets/icons";
import { alertPermission, sn } from "utils";

import { useForm, Controller } from "react-hook-form";
import { useRecoilValue } from "recoil";
import tinycolor from "tinycolor2";

import { authAtom } from "stores";
import css from "styles";

import {
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from "clients/apis";
import { _goToEmployeePermissions } from "routers/helper";
import { useChmod, useFlag } from "hooks";
import CheckBox from "./CheckBox";

type CreateEmployeeForm = {
  countryName?: string;
  countryIsoCode2?: string;
  countryIsoCode3?: string;
};

const EmployeeCard: React.FC<{ employee: EmployeeT; roles: Array<RoleT> }> = ({
  employee,
  roles,
}) => {
  const {
    company: { color },
  } = useRecoilValue(authAtom);

  const { chmod } = useChmod();
  const user = useRecoilValue(authAtom);

  const {
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateEmployeeForm>({
    defaultValues: {
      name: employee?.user?.name,
      mobile: employee?.user?.mobile,
      role: employee?.role,
      force: 0,
    },
  });

  const { flg: edit, on: goToEdit, off: closeEdit } = useFlag();
  const { flg: remove, on: goToRemove, off: closeRemove } = useFlag();

  const createEmployee = useCreateEmployee();
  const updateEmployeeMut = useUpdateEmployee();
  const deleteEmployeeMut = useDeleteEmployee();

  useEffect(() => setValue("role", employee?.role), [employee?.role_id]);

  const _onHide = () => {
    reset();
    closeEdit();
  };

  const _onSubmit = () =>
    employee?.id
      ? chmod("company", "PUT")
        ? _onUpdate({ id: employee.id, ...getValues() })
        : alertPermission
      : chmod("company", "POST")
      ? _onCreate({ ...getValues() })
      : alertPermission;

  const _onCreate = (data: CreateEmployeeReqT) =>
    createEmployee.mutate(data, { onSuccess: _onHide });

  const _onUpdate = (data: UpdateEmployeeReqT) =>
    updateEmployeeMut.mutate(data, { onSuccess: _onHide });

  const _onDeleteConfirm = (close: () => void) => {
    close();
    reset();
    deleteEmployeeMut.mutate({ id: employee?.id });
  };

  const _renderContent = () => {
    if (!employee) {
      return (
        <TouchableOpacity
          onPress={chmod("company", "POST") ? goToEdit : alertPermission}
          activeOpacity={sizes.tapOpacity}
          style={styles.plusContainer}
        >
          <ImgIcon icon={icons._plus} size={sn(25, 28, 30, 30)} />
        </TouchableOpacity>
      );
    }

    return (
      <>
        <View style={styles.header}>
          <Text style={{ lineHeight: 20 }} numberOfLines={1} weight="bold">
            {employee?.user?.name ?? employee?.user?.mobile}
          </Text>
          <Text style={{ lineHeight: 20 }} numberOfLines={1}>
            {employee?.role?.name}
          </Text>
        </View>
        <View style={styles.footer}>
          <>
            <TouchableOpacity
              onPress={() =>
                _goToEmployeePermissions({
                  user_id: employee?.user?.id,
                  name: employee?.user?.name,
                })
              }
              activeOpacity={sizes.tapOpacity}
              style={styles.actions}
            >
              <Icon
                size={18}
                type="simpleLine"
                name="lock"
                color={colors.success}
              />
            </TouchableOpacity>
            <Separator axis="y" size={"90%"} />

            <TouchableOpacity
              onPress={goToEdit}
              activeOpacity={sizes.tapOpacity}
              style={styles.actions}
            >
              <Icon
                size={18}
                type="simpleLine"
                name="pencil"
                color={colors.theme}
              />
            </TouchableOpacity>

            <Separator axis="y" size={"90%"} />
            <TouchableOpacity
              onPress={
                chmod("company", "DELETE") && employee?.user_id !== user?.id
                  ? goToRemove
                  : alertPermission
              }
              activeOpacity={sizes.tapOpacity}
              style={styles.actions}
            >
              <Icon
                size={18}
                name="trash"
                type="simpleLine"
                color={colors.danger}
              />
            </TouchableOpacity>
          </>
        </View>
      </>
    );
  };

  return (
    <>
      <Shadow style={styles.shadowCard}>{_renderContent()}</Shadow>

      <Modal visible={edit} close={_onHide}>
        <InputLabel label={strings.name} />
        <Controller
          control={control}
          name="name"
          rules={{ required: { value: true, message: strings.fill_input } }}
          render={({ field: { value, onChange } }) => (
            <Input
              editable={!employee?.id}
              value={value}
              onChangeText={onChange}
              imgIcon={{ icon: icons._role }}
              placeholder={strings.insert_employee_name}
              error={errors?.name?.message}
            />
          )}
        />

        <InputLabel label={strings.mobile} />
        <Controller
          control={control}
          name="mobile"
          rules={{
            required: { value: true, message: strings.fill_input },
            pattern: {
              value: patterns.mobile,
              message: strings.invalid_mobile,
            },
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              editable={!employee?.id}
              value={value}
              onChangeText={onChange}
              imgIcon={{ icon: icons._role }}
              placeholder={strings.insert_employee_mobile}
              maxLength={11}
              keyboardType="phone-pad"
              error={errors?.mobile?.message}
            />
          )}
        />

        <InputLabel label={strings.role} />
        <Controller
          control={control}
          name="role"
          rules={{
            required: { value: true, message: strings.pick_employee_role },
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <>
                <GridView
                  spacing={sizes.space / 2}
                  gridHeight={70}
                  numberOfCol={sn(3, 3, 4, 5)}
                  data={roles}
                  renderItem={(item) => {
                    const isSelected = item?.id === value?.id;

                    const _onSelect = () => onChange(item);

                    return (
                      <TouchableOpacity
                        style={css.cover}
                        onPress={_onSelect}
                        activeOpacity={sizes.tapOpacity}
                      >
                        <Shadow
                          shadow={{
                            radius: 2,
                            color: isSelected ? color : colors.dark._10,
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: sizes.radius.lg,
                            backgroundColor: isSelected
                              ? tinycolor(color).lighten(50).toString()
                              : colors.white,
                            paddingHorizontal: 5,
                          }}
                        >
                          <Text
                            weight={isSelected ? "bold" : "default"}
                            color={isSelected ? color : colors.text.normal}
                            align="center"
                          >
                            {item?.name}
                          </Text>
                        </Shadow>
                      </TouchableOpacity>
                    );
                  }}
                />
                {errors?.role?.message && (
                  <Text size={"xs"} color={colors.danger}>
                    {errors?.role?.message}
                  </Text>
                )}
              </>
            );
          }}
        />

        <View style={{ marginVertical: 15 }}>
          {employee?.id && (
            <Controller
              control={control}
              name="force"
              render={({ field: { value, onChange } }) => {
                return (
                  <CheckBox
                    value={value}
                    onChange={onChange}
                    title="مجوزهای نقش برای کارمند اعمال شود"
                  />
                );
              }}
            />
          )}
        </View>

        <Button
          title={{ value: strings.confirm }}
          mt={sizes.space}
          onPress={handleSubmit(_onSubmit)}
          loading={createEmployee.isLoading || updateEmployeeMut.isLoading}
        />
      </Modal>

      <ConfirmAlert
        force
        show={remove}
        hide={closeRemove}
        renderContent={(close) => (
          <DeleteAlertComponent
            title={`آیا میخواهید کارمند:  ${employee?.user?.name} را حذف کنید؟ `}
            remove={() => _onDeleteConfirm(close)}
            cancel={close}
          />
        )}
      />

      {deleteEmployeeMut.isLoading && <LoadingBox />}
    </>
  );
};

export default EmployeeCard;

const styles = StyleSheet.create({
  shadowCard: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    alignItems: "center",
    borderRadius: sizes.radius.xl,
    backgroundColor: colors.white,
    justifyContent: "space-evenly",
  },

  header: {
    flex: 1,
    padding: sizes.space,
    alignItems: "center",
    justifyContent: "center",
  },

  footer: {
    width: "100%",
    height: sn(40, 40, 45, 45),
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row-reverse",
  },

  actions: { flex: 1, alignItems: "center", justifyContent: "center" },

  plusContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },

  row: { flexDirection: "row-reverse", alignSelf: "center" },
});
